import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as systemActions from '@redux/modules/system'
import type { EntityOptions } from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import defaultFormState, { requiredFields } from '@models/system'
import type { DataStoreItemKeyModel, SystemModel, TemplateQualityControlCategoryModel } from '@models/types'

const stringifyObjectKeys: (keyof SystemModel)[] = ['options']

export const generateUrls = (system: Partial<SystemModel> = {}) => ({
  editSystemRelationsUrl: `#/systemManager/systems/${system.id}/edit/relations`,
  editSystemUrl: `#/systemManager/systems/${system.id}/edit`,
  systemsIndexUrl: '#/systemManager/systems',
})

type CreateSystemPayload = {
  dispatch: AppDispatch,
  systemParams: Partial<SystemModel>,
  requestOptions?: EntityOptions,
}

const createSystem = (params: CreateSystemPayload) => {
  const { dispatch, systemParams, requestOptions } = params
  const { createSystem: createFn } = systemActions

  const updatedParams = mergeAndStringifyEntityObjects({}, systemParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions)).then((response) => {
    const { success } = response

    if (success){
      const { data } = response
      const { editSystemUrl } = generateUrls(data)

      return {
        data,
        redirectUrl: editSystemUrl,
        success,
      }
    }

    return response
  })
}

type UpdateSystemPayload = {
  dispatch: AppDispatch,
  system: SystemModel,
  systemParams: Partial<SystemModel>,
  requestOptions?: EntityOptions,
}

const updateSystem = (params: UpdateSystemPayload) => {
  const {
    dispatch, system, systemParams, requestOptions,
  } = params

  const { updateSystem: updateFn } = systemActions

  const updatedParams = mergeAndStringifyEntityObjects(system, systemParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

export type ShowCreateOrEditQualityControlCategoryParams = {
  category?: TemplateQualityControlCategoryModel,
  system: SystemModel,
}

export type ShowManageSystemMaintenanceModalParams = {
  system: SystemModel,
}

type ManageSystemMaintenanceParams = {
  system: SystemModel,
  showManageSystemMaintenanceModal: (payload: ShowManageSystemMaintenanceModalParams) => void,
}

const manageSystemMaintenance = (params: ManageSystemMaintenanceParams) => new Promise((resolve, reject) => {
  const { system, showManageSystemMaintenanceModal } = params

  if (showManageSystemMaintenanceModal){
    const payload = {
      system,
    }

    showManageSystemMaintenanceModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showManageSystemMaintenanceModal not defined in PageContext callbacks'))
})

export type ShowCreateOrEditDataStoreItemKeyModalParams = {
  dataStoreItemKey?: DataStoreItemKeyModel,
  system: SystemModel,
}

type CreateOrEditDataStoreItemKey = {
  dataStoreItemKey?: DataStoreItemKeyModel,
  system: SystemModel,
  showCreateOrEditDataStoreItemKeyModal: (payload: ShowCreateOrEditDataStoreItemKeyModalParams) => void,
}

const createOrEditDataStoreItemKey = (params: CreateOrEditDataStoreItemKey) => new Promise((resolve, reject) => {
  const {
    dataStoreItemKey, showCreateOrEditDataStoreItemKeyModal, system,
  } = params

  if (showCreateOrEditDataStoreItemKeyModal){
    const payload = {
      dataStoreItemKey,
      system,
    }

    showCreateOrEditDataStoreItemKeyModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditDataStoreItemKeyModal not defined in PageContext callbacks'))
})

export type ShowSlackWebhookSettingsModalParams = {
  system: SystemModel,
}

type ManageSlackWebhookSettings = {
  system: SystemModel,
  showSlackWebhookSettingsModal: (payload: ShowSlackWebhookSettingsModalParams) => void,
}

const manageSlackWebhookSettings = (params: ManageSlackWebhookSettings) => new Promise((resolve, reject) => {
  const { showSlackWebhookSettingsModal, system } = params

  if (showSlackWebhookSettingsModal){
    const payload = {
      system,
    }

    showSlackWebhookSettingsModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showSlackWebhookSettingsModal not defined in PageContext callbacks'))
})

export type ShowManageSystemIntegrationsModalParams = {
  system: Partial<SystemModel>,
}

type ManageSystemIntegrations = {
  system: SystemModel,
  showManageSystemIntegrationsModal: (payload: ShowManageSystemIntegrationsModalParams) => void,
}

const manageSystemIntegrations = (params: ManageSystemIntegrations) => new Promise((resolve, reject) => {
  const { system, showManageSystemIntegrationsModal } = params

  if (showManageSystemIntegrationsModal){
    const payload = {
      system,
    }

    showManageSystemIntegrationsModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showManageSystemIntegrationsModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useSystemForm(
  system: Partial<SystemModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options

  const systemForm = useForm(
    defaultFormState,
    { entity: system, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [system.id, system.cache_key],
  )

  return systemForm
}

function useSystem(initEntity: Partial<SystemModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: system }: { entity: SystemModel } = useLatestEntity(initEntity, 'systems')

  const dispatch = useDispatch()

  const { creating, loading, updating } = useSelector(reduxState => reduxState.systems)

  const { callbacks } = useContext(PageContext)
  const {
    showCreateOrEditDataStoreItemKeyModal,
    showManageSystemIntegrationsModal,
    showManageSystemMaintenanceModal,
    showSlackWebhookSettingsModal,
  } = callbacks || {}

  return {
    callbacks: {
      createOrEditDataStoreItemKey: (dataStoreItemKey?: DataStoreItemKeyModel) => createOrEditDataStoreItemKey({
        dataStoreItemKey, showCreateOrEditDataStoreItemKeyModal, system,
      }),
      createOrEditQualityControlCategory: (category?: TemplateQualityControlCategoryModel) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditTemplateQualityControlCategoryModal',
        payload: { category, system },
      }),
      createSystem: (systemParams: Partial<SystemModel>, requestOptions?: EntityOptions) => (
        createSystem({ systemParams, dispatch, requestOptions })
      ),
      manageSlackWebhookSettings: () => manageSlackWebhookSettings({ showSlackWebhookSettingsModal, system }),
      manageSystemIntegrations: () => manageSystemIntegrations({ system, showManageSystemIntegrationsModal }),
      manageSystemMaintenance: () => manageSystemMaintenance({ system, showManageSystemMaintenanceModal }),
      updateSystem: (systemParams: Partial<SystemModel>, requestOptions?: EntityOptions) => updateSystem({
        system, systemParams, dispatch, requestOptions,
      }),
    },
    creating,
    loading,
    system,
    updating,
    urls: generateUrls(system),
  }
}

export default useSystem
